import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, downloadFile } from "@/lib/api";
import { fmtEUR, fmtDateTime, fmtDate } from "@/lib/format";
import { Seizoenspatroon } from "@/components/Seizoenspatroon";
import { metKeys, zonderKeys, nieuweRijKey } from "@/lib/rijkeys";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { ArrowLeft, AlertTriangle, Package, Trash2, Plus, X, Languages, Boxes, Archive, ArchiveRestore, FileText, ShoppingBag, ExternalLink, Sparkles, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useDetailNav } from "@/lib/overzichtNav";
import { OverzichtNav } from "@/components/OverzichtNav";
import { ConfirmDeleteDialog } from "@/components/ConfirmDelete";
import { ProductKlantcodes } from "@/components/ProductKlantcodes";
import { ProductFotos } from "@/components/ProductFotos";
import { MoederKoppeling } from "@/components/MoederKoppeling";
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from "recharts";

// Landen die veel worden gebruikt in de klantendatabase — vrij typen mag ook
const STANDAARD_LANDEN = [
  "Nederland", "België", "Duitsland", "Frankrijk", "Verenigd Koninkrijk", "Oostenrijk",
  "Zwitserland", "Luxemburg", "Spanje", "Italië", "Denemarken", "Zweden", "Noorwegen", "Polen",
];

// Vaste landen waar we per-land verkoop willen configureren op de webshop.
// Extra landen kunnen later via een aparte "Land toevoegen"-knop worden gesteund;
// voor nu focussen we op deze 4.
const WEBSHOP_LANDEN = ["Nederland", "België", "Frankrijk", "Duitsland"];
// Speciale rijen — géén landprijzen-entries maar de brede regio-flags op product.
// De switch mapt naar `verkoop_eu` / `verkoop_buiten_eu` op product-niveau.
const REGIO_ROWS = [
  { key: "eu",         label: "Overige EU-landen",  hint: "IT, ES, PL, PT, …",       veld: "verkoop_eu" },
  { key: "buiten_eu",  label: "Buiten EU",          hint: "GB, US, CH, JP, …",       veld: "verkoop_buiten_eu" },
];

function LandRij({ initieel, actief, onCommit, onToggle, onVerwijderen, verwijderbaar, testidBase }) {
  // Lokale draft-state — voorkomt dat elke keystroke een server-PATCH triggert
  // en dat de response de user-input overschrijft. Sync alleen bij verandering
  // van externe input (bv. na delete/add van een land, of bij initiële load).
  const [verkoopprijs, setVerkoopprijs] = React.useState(initieel.verkoopprijs);
  const [advies, setAdvies] = React.useState(initieel.consument_adviesprijs);
  const [btw, setBtw] = React.useState(initieel.btw_pct_lokaal);
  const initRef = React.useRef(initieel);
  React.useEffect(() => {
    // Alleen resyncen als de externe waarde daadwerkelijk anders is (bv. na add/delete)
    if (initieel.verkoopprijs !== initRef.current.verkoopprijs) setVerkoopprijs(initieel.verkoopprijs);
    if (initieel.consument_adviesprijs !== initRef.current.consument_adviesprijs) setAdvies(initieel.consument_adviesprijs);
    if (initieel.btw_pct_lokaal !== initRef.current.btw_pct_lokaal) setBtw(initieel.btw_pct_lokaal);
    initRef.current = initieel;
  }, [initieel]);

  const commit = (veld, waarde) => {
    const parse = (v) => (v === "" || v == null ? "" : v);
    const next = { verkoopprijs, consument_adviesprijs: advies, btw_pct_lokaal: btw, [veld]: waarde };
    // Alleen daadwerkelijk PATCHen als het veld gewijzigd is t.o.v. de initiële externe waarde
    if (parse(waarde) === parse(initRef.current[veld])) return;
    onCommit({
      verkoopprijs: parse(next.verkoopprijs),
      consument_adviesprijs: parse(next.consument_adviesprijs),
      btw_pct_lokaal: parse(next.btw_pct_lokaal),
    });
  };

  const dim = !actief;
  return (
    <>
      <div className={dim ? "text-muted-foreground line-through" : ""}>{initieel.land}</div>
      <div className="flex justify-center">
        <Switch checked={actief} onCheckedChange={onToggle}
          data-testid={`${testidBase}-actief`} />
      </div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
        <Input type="number" step="0.01" min="0"
          value={verkoopprijs}
          onChange={(e) => setVerkoopprijs(e.target.value)}
          onBlur={(e) => commit("verkoopprijs", e.target.value)}
          disabled={!actief}
          className="pl-7 h-9"
          placeholder="—"
          data-testid={`${testidBase}-verkoopprijs`} />
      </div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
        <Input type="number" step="0.01" min="0"
          value={advies}
          onChange={(e) => setAdvies(e.target.value)}
          onBlur={(e) => commit("consument_adviesprijs", e.target.value)}
          disabled={!actief}
          className="pl-7 h-9"
          placeholder="—"
          data-testid={`${testidBase}-advies`} />
      </div>
      <div className="relative">
        <Input type="number" step="0.1" min="0" max="100"
          value={btw}
          onChange={(e) => setBtw(e.target.value)}
          onBlur={(e) => commit("btw_pct_lokaal", e.target.value)}
          disabled={!actief}
          className="pr-7 h-9"
          placeholder="—"
          data-testid={`${testidBase}-btw`} />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
      </div>
      <div>
        {verwijderbaar && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onVerwijderen}
            data-testid={`${testidBase}-verwijder`}>
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </div>
    </>
  );
}

function LandprijzenBlok({ p, patch }) {
  // Merge de vaste 4 landen met eventuele extra landen die al in de DB staan.
  const bestaande = p.landprijzen || [];
  const extraLanden = bestaande
    .map(r => r.land)
    .filter(l => l && !WEBSHOP_LANDEN.includes(l));
  const alleLanden = [...WEBSHOP_LANDEN, ...extraLanden];
  const [nieuwLandOpen, setNieuwLandOpen] = React.useState(false);
  const [nieuwLandKeuze, setNieuwLandKeuze] = React.useState("");
  // Suggesties = STANDAARD_LANDEN minus de al-getoonde
  const suggesties = STANDAARD_LANDEN.filter(l => !alleLanden.includes(l));

  const getRow = (land) => {
    const bestaand = bestaande.find(r => (r.land || "").toLowerCase() === land.toLowerCase()) || {};
    const verkoopprijs = bestaand.verkoopprijs ?? bestaand.prijs ?? "";
    return {
      land,
      actief: bestaand.actief !== false,
      verkoopprijs,
      consument_adviesprijs: bestaand.consument_adviesprijs ?? "",
      btw_pct_lokaal: bestaand.btw_pct_lokaal ?? "",
    };
  };

  const zet = (land, patchRow) => {
    const rest = bestaande.filter(r => (r.land || "").toLowerCase() !== land.toLowerCase());
    const huidig = getRow(land);
    const nieuw = { ...huidig, ...patchRow };
    const opgeslagen = {
      land: nieuw.land,
      actief: nieuw.actief !== false,
      ...(nieuw.verkoopprijs !== "" && nieuw.verkoopprijs != null
        ? { verkoopprijs: Number(nieuw.verkoopprijs) } : {}),
      ...(nieuw.consument_adviesprijs !== "" && nieuw.consument_adviesprijs != null
        ? { consument_adviesprijs: Number(nieuw.consument_adviesprijs) } : {}),
      ...(nieuw.btw_pct_lokaal !== "" && nieuw.btw_pct_lokaal != null
        ? { btw_pct_lokaal: Number(nieuw.btw_pct_lokaal) } : {}),
    };
    patch("landprijzen", [...rest, opgeslagen]);
  };

  const zetVelden = (land, velden) => {
    // velden = {verkoopprijs, consument_adviesprijs, btw_pct_lokaal} — allemaal
    // strings ('' of getal-string). Bouw het opgeslagen record en PATCH.
    const rest = bestaande.filter(r => (r.land || "").toLowerCase() !== land.toLowerCase());
    const huidig = getRow(land);
    const opgeslagen = {
      land,
      actief: huidig.actief !== false,
      ...(velden.verkoopprijs !== "" && velden.verkoopprijs != null
        ? { verkoopprijs: Number(velden.verkoopprijs) } : {}),
      ...(velden.consument_adviesprijs !== "" && velden.consument_adviesprijs != null
        ? { consument_adviesprijs: Number(velden.consument_adviesprijs) } : {}),
      ...(velden.btw_pct_lokaal !== "" && velden.btw_pct_lokaal != null
        ? { btw_pct_lokaal: Number(velden.btw_pct_lokaal) } : {}),
    };
    patch("landprijzen", [...rest, opgeslagen]);
  };

  const zetActief = (land, v) => zet(land, { actief: v });

  const verwijderLand = (land) => {
    if (WEBSHOP_LANDEN.includes(land)) return;   // vaste landen nooit verwijderen
    patch("landprijzen", bestaande.filter(r => (r.land || "").toLowerCase() !== land.toLowerCase()));
  };

  const voegLandToe = (land) => {
    const naam = (land || "").trim();
    if (!naam) return;
    if (alleLanden.some(l => l.toLowerCase() === naam.toLowerCase())) {
      toast.error(`${naam} staat al in de lijst`);
      return;
    }
    // Start met actief=true zonder waarden zodat het meteen in de tabel verschijnt
    patch("landprijzen", [...bestaande, { land: naam, actief: true }]);
    setNieuwLandKeuze("");
    setNieuwLandOpen(false);
  };

  return (
    <div>
      <div className="mb-3">
        <h4 className="text-sm font-medium">Verkoop per land</h4>
        <p className="text-xs text-muted-foreground">
          Voor <strong>Nederland, België, Frankrijk en Duitsland</strong> kun je per land specifieke prijzen instellen
          (leeg = valt terug op de standaardprijs / adviesprijs hierboven). De <strong>overige EU</strong> en <strong>buiten EU</strong>
          switches bepalen of het product in die regio verschijnt zonder specifieke prijs.
          Klantspecifieke prijzen hebben áltijd voorrang.
        </p>
        <p className="text-[11px] text-muted-foreground mt-1">
          <strong>Let op — BTW %:</strong> uitsluitend informatief voor de retailer om zijn marge te berekenen.
          Géén invloed op de BTW-berekening op onze eigen factuur (die volgt uit het klant-BTW-regime).
        </p>
      </div>
      <div className="grid grid-cols-[minmax(160px,1fr)_auto_150px_150px_110px_32px] items-center gap-x-3 gap-y-2 text-sm">
        <div className="text-xs text-muted-foreground font-medium">Land / regio</div>
        <div className="text-xs text-muted-foreground font-medium text-center">Aan/uit</div>
        <div className="text-xs text-muted-foreground font-medium">Verkoopprijs (€)</div>
        <div className="text-xs text-muted-foreground font-medium">Adviesprijs (€)</div>
        <div className="text-xs text-muted-foreground font-medium">BTW % lokaal</div>
        <div></div>
        {alleLanden.map((land) => {
          const r = getRow(land);
          const verwijderbaar = !WEBSHOP_LANDEN.includes(land);
          const testidBase = `landprijs-${land.toLowerCase().replace(/\W/g, "")}`;
          return (
            <LandRij
              key={land}
              initieel={r}
              actief={r.actief}
              onToggle={(v) => zetActief(land, v)}
              onCommit={(velden) => zetVelden(land, velden)}
              onVerwijderen={() => verwijderLand(land)}
              verwijderbaar={verwijderbaar}
              testidBase={testidBase}
            />
          );
        })}
        {/* Grofmazige regio-rijen — mappen naar product-brede booleans i.p.v. landprijzen */}
        {REGIO_ROWS.map((rr) => {
          const aan = p[rr.veld] !== false;
          return (
            <React.Fragment key={rr.key}>
              <div>
                <div className={aan ? "" : "text-muted-foreground line-through"}>{rr.label}</div>
                <div className="text-[11px] text-muted-foreground">{rr.hint}</div>
              </div>
              <div className="flex justify-center">
                <Switch checked={aan}
                  onCheckedChange={(v) => patch(rr.veld, v)}
                  data-testid={`prod-verkoop-${rr.key}`} />
              </div>
              <div className="col-span-3 text-[11px] text-muted-foreground italic">
                Prijzen vallen terug op de standaard verkoopprijs / adviesprijs.
              </div>
              <div></div>
            </React.Fragment>
          );
        })}
      </div>
      <div className="mt-3">
        {nieuwLandOpen ? (
          <div className="flex items-center gap-2">
            <Input
              list="landprijs-suggesties-nieuw"
              autoFocus
              value={nieuwLandKeuze}
              onChange={(e) => setNieuwLandKeuze(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") voegLandToe(nieuwLandKeuze);
                if (e.key === "Escape") { setNieuwLandOpen(false); setNieuwLandKeuze(""); }
              }}
              placeholder="Bijv. Italië, Spanje, Polen…"
              className="max-w-xs h-9"
              data-testid="landprijs-nieuw-input"
            />
            <datalist id="landprijs-suggesties-nieuw">
              {suggesties.map((l) => <option key={l} value={l} />)}
            </datalist>
            <Button size="sm" onClick={() => voegLandToe(nieuwLandKeuze)}
              data-testid="landprijs-nieuw-bevestigen">Toevoegen</Button>
            <Button size="sm" variant="ghost"
              onClick={() => { setNieuwLandOpen(false); setNieuwLandKeuze(""); }}>
              Annuleer
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm"
            onClick={() => setNieuwLandOpen(true)}
            data-testid="landprijs-nieuw-open">
            <Plus className="h-4 w-4 mr-1" /> Land toevoegen
          </Button>
        )}
      </div>
    </div>
  );
}

// Eén-klik inboeken van een (te late) order bij de fabrikant: china → leverancier of warehouse.
function ChinaInboeken({ productId, aantal, onDone }) {
  const [busy, setBusy] = useState(false);
  const [deel, setDeel] = useState("");        // welke knop draait
  const boek = async (bestemming) => {
    if (!window.confirm(`${aantal} stuks inboeken bij ${bestemming === "warehouse" ? "het eigen warehouse" : "de leverancier"}?`)) return;
    setBusy(true); setDeel(bestemming);
    try {
      await api.post(`/producten/${productId}/china-inboeken`, { bestemming });
      toast.success(`${aantal} stuks ingeboekt bij ${bestemming === "warehouse" ? "warehouse" : "leverancier"}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Inboeken mislukt");
    } finally { setBusy(false); setDeel(""); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button size="sm" className="h-7 px-2 text-xs" disabled={busy}
        onClick={() => boek("leverancier")} data-testid="china-inboeken-leverancier">
        {busy && deel === "leverancier" ? "Bezig…" : "Inboeken bij leverancier"}
      </Button>
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs bg-white" disabled={busy}
        onClick={() => boek("warehouse")} data-testid="china-inboeken-warehouse">
        {busy && deel === "warehouse" ? "Bezig…" : "Inboeken in warehouse"}
      </Button>
      <span className="text-[11px] opacity-80">Boekt {aantal} stuks over en zet een binnenkomst-piek in de grafiek.</span>
    </div>
  );
}

function VoorraadForecast({ forecast, historieWeken, onHistorieChange, modus, onModusChange, productId, onIngeboekt }) {
  if (!forecast || !forecast.punten?.length) return null;
  const { punten, kritisch_punt, weekverkoop, onder_kritisch_week, bestel_label,
          bestel_datum, bestel_weken, oos_datum, oos_weken, oos_label, oos_buiten_horizon,
          lead_weken, huidige_voorraad } = forecast;
  const chinaWeek = punten.find((p) => p.china_binnenkomst > 0);
  const inboekWeken = punten.filter((p) => (p.inboek || 0) > 0);
  const metingen = punten.filter((p) => p.meting != null);
  const xInterval = Math.max(0, Math.ceil(punten.length / 14) - 1);
  const totaalVerkocht = punten.reduce((s, p) => s + (p.verkocht || 0), 0);
  const totaalDirect = punten.reduce((s, p) => s + (p.verkocht_direct || 0), 0);
  if (!weekverkoop) {
    return (
      <div className="bg-card border border-border rounded-xl p-6" data-testid="voorraad-forecast">
        <h3 className="font-display text-xl font-medium mb-1">Voorraadprognose</h3>
        <p className="text-sm text-muted-foreground">Nog onvoldoende verkoopdata (12 mnd) om een prognose te tekenen.</p>
      </div>
    );
  }
  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="voorraad-forecast">
      <div className="flex items-start justify-between gap-4 mb-1 flex-wrap">
        <h3 className="font-display text-xl font-medium">
          {modus === "oos" ? "Wanneer ben ik uitverkocht?" : modus === "historie" ? "Voorraad- en verkoophistorie" : "Bestelmoment"}
        </h3>
        <div className="flex items-center gap-1" data-testid="forecast-modus">
          {[{ l: "Bestelmoment", v: "bestel" }, { l: "Out of stock", v: "oos" }, { l: "Historie", v: "historie" }].map((o) => (
            <Button key={o.v} size="sm" variant={modus === o.v ? "default" : "outline"}
              className="h-7 px-2 text-xs" onClick={() => onModusChange?.(o.v)}
              data-testid={`modus-${o.v}`}>
              {o.l}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 my-4" data-testid="forecast-kpis">
        <div className="rounded-lg border border-border p-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Huidige voorraad</div>
          <div className="text-2xl font-medium tabular-nums">{huidige_voorraad}</div>
          <div className="text-xs text-muted-foreground">warehouse + leverancier · {weekverkoop}/week verkoop</div>
        </div>
        <div className={`rounded-lg border p-3 ${bestel_weken === 0 ? "border-orange-300 bg-orange-50" : "border-border"}`}
          data-testid="kpi-bestelmoment">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Bestel uiterlijk</div>
          <div className="text-2xl font-medium">{bestel_datum ? fmtDate(bestel_datum) : "—"}</div>
          <div className="text-xs text-muted-foreground">
            {bestel_weken === 0 ? "nu bestellen" : bestel_weken != null ? `over ${bestel_weken} wk` : `niet binnen ${forecast.horizon_weken} wk`}
          </div>
        </div>
        <div className={`rounded-lg border p-3 ${oos_weken != null && oos_weken <= (lead_weken || 0) ? "border-red-300 bg-red-50" : "border-border"}`}
          data-testid="kpi-oos">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Out of stock</div>
          <div className="text-2xl font-medium">{oos_datum ? fmtDate(oos_datum) : "—"}</div>
          <div className="text-xs text-muted-foreground">
            {oos_weken != null ? `over ${oos_weken} wk` : oos_buiten_horizon ? `niet binnen ${forecast.horizon_weken} wk` : "—"}
          </div>
        </div>
        <div className="rounded-lg border border-border p-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Kritisch punt</div>
          <div className="text-2xl font-medium tabular-nums">{kritisch_punt}</div>
          <div className="text-xs text-muted-foreground">= {lead_weken} wk levertijd</div>
        </div>
      </div>

      {modus === "historie" && (
        <div className="flex items-center gap-1 mb-2" data-testid="historie-periode">
          {[{ l: "Auto", v: null }, { l: "1 jaar", v: 52 }, { l: "2 jaar", v: 104 }, { l: "5 jaar", v: 260 }].map((o) => (
            <Button key={o.l} size="sm" variant={historieWeken === o.v ? "default" : "outline"}
              className="h-7 px-2 text-xs" onClick={() => onHistorieChange?.(o.v)}
              data-testid={`historie-${o.v || "auto"}`}>
              {o.l}
            </Button>
          ))}
        </div>
      )}

      {forecast.china_te_laat && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 mb-3 space-y-2"
          data-testid="forecast-china-te-laat">
          <div>
            De order bij de fabrikant ({forecast.china_aantal} stuks) had al binnen moeten zijn. Zolang die niet is
            ingeboekt telt hij <strong>niet</strong> mee als aanwezige voorraad — in de grafiek staat hij als verwachte
            binnenkomst volgende week.
          </div>
          <ChinaInboeken productId={productId} aantal={forecast.china_aantal} onDone={onIngeboekt} />
        </div>
      )}
      <p className="text-xs text-muted-foreground mb-4">
        {modus === "bestel" && (
          <>
            Bestel uiterlijk <span className="text-foreground font-medium">{bestel_datum ? fmtDate(bestel_datum) : "—"}</span>{" "}
            om niet zonder voorraad te komen: dan zakt de voorraad tot het kritische punt van{" "}
            <span className="text-foreground font-medium">{kritisch_punt}</span> stuks ({lead_weken} weken levertijd ×{" "}
            {weekverkoop}/week).
            {oos_datum ? <> Zonder nieuwe levering ben je uitverkocht op <span className="text-foreground font-medium">{fmtDate(oos_datum)}</span>.</> : null}
          </>
        )}
        {modus === "oos" && (
          <>
            {oos_datum
              ? <>Bij het huidige tempo van {weekverkoop}/week is de voorraad op{" "}
                  <span className="text-foreground font-medium">{fmtDate(oos_datum)}</span> (over {oos_weken} weken) uitverkocht.</>
              : <>Bij het huidige tempo van {weekverkoop}/week blijft er de komende {forecast.horizon_weken} weken voorraad.</>}
            {chinaWeek ? ` De order bij de fabrikant (+${chinaWeek.china_binnenkomst}) komt binnen in ${chinaWeek.week} en verschuift die datum.` : ""}
          </>
        )}
        {modus === "historie" && (
          <>
            Grijze lijn = werkelijke voorraad. Waar een geïmporteerde meting bestaat is dat het ankerpunt; daarna
            wordt verder gerekend met verkopen en ingeboekte voorraad. Groene stippellijn = prognose obv gem.
            <span className="text-foreground font-medium"> {weekverkoop}/week</span>.
            {inboekWeken.length ? ` Paarse staaf = ingeboekte voorraad (${inboekWeken.map((w) => `${w.week}: +${w.inboek}`).join(", ")}).` : ""}
            {metingen.length ? ` Blauwe stip = geïmporteerde voorraadstand (${metingen.map((m) => `${m.week}: ${m.meting}`).join(", ")}).` : ""}
            {` Grijze staafjes = verkopen per week (rechteras), totaal ${totaalVerkocht} stuks in deze periode.`}
            {totaalDirect ? ` Blauwe staafjes = direct uit de binnenkomende zending geleverd (${totaalDirect} stuks) — die zijn niet van de voorraad afgehaald.` : ""}
          </>
        )}
        {onder_kritisch_week && modus !== "bestel" ? ` ⚠️ Voorraad zakt onder kritisch rond wk ${onder_kritisch_week}.` : ""}
      </p>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={punten} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="week" tick={{ fontSize: 11 }} interval={xInterval} />
          <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} width={34} />
          <Tooltip formatter={(v, n) => [v, n === "china_binnenkomst" ? "Order fabrikant" : n === "inboek" ? "Voorraad ingeboekt" : n === "meting" ? "Gemeten voorraad (import)" : n === "verkocht" ? "Verkocht deze week" : n === "verkocht_direct" ? "Direct geleverd (niet uit voorraad)" : n === "historie" ? "Werkelijke voorraad" : "Verwachte voorraad"]}
            labelFormatter={(l) => {
              const p = punten.find((x) => x.week === l);
              return p?.datum ? `${l} — ${fmtDate(p.datum)}` : l;
            }} />
          {modus === "historie" && (
            <Bar yAxisId="right" dataKey="verkocht" name="Verkocht deze week" fill="#cbd5e1" barSize={punten.length > 60 ? 3 : 10} isAnimationActive={false} />
          )}
          {modus === "historie" && (
            <Bar yAxisId="right" dataKey="verkocht_direct" name="Direct geleverd (niet uit voorraad)" fill="#38bdf8" barSize={punten.length > 60 ? 3 : 10} isAnimationActive={false} />
          )}
          <Bar yAxisId="left" dataKey="china_binnenkomst" name="Order fabrikant" fill="#f59e0b" barSize={14} />
          <Bar yAxisId="left" dataKey="inboek" name="Voorraad ingeboekt" fill="#7c3aed" barSize={14} />
          <Line yAxisId="left" type="monotone" dataKey="historie" name="Werkelijke voorraad" stroke="#475569" strokeWidth={2} dot={false} connectNulls={false} />
          <Line yAxisId="left" type="monotone" dataKey="voorraad" name="Verwachte voorraad" stroke="#0f766e" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls={false} />
          {modus === "historie" && (
            <Line yAxisId="left" dataKey="meting" name="Gemeten voorraad (import)" stroke="#0284c7" strokeWidth={0}
              dot={{ r: 5, fill: "#0284c7", stroke: "#fff", strokeWidth: 1.5 }} connectNulls={false} isAnimationActive={false} />
          )}
          <ReferenceLine yAxisId="left" y={kritisch_punt} stroke="#dc2626" strokeDasharray="5 5"
            label={{ value: `Kritisch ${kritisch_punt}`, position: "insideTopRight", fontSize: 10, fill: "#dc2626" }} />
          {bestel_label && (
            <ReferenceLine yAxisId="left" x={bestel_label} stroke="#ea580c" strokeWidth={1.5}
              label={{ value: bestel_datum ? `Bestel ${fmtDate(bestel_datum)}` : "Bestel", position: "top", fontSize: 10, fill: "#ea580c" }} />
          )}
          {modus !== "historie" && oos_label && (
            <ReferenceLine yAxisId="left" x={oos_label} stroke="#dc2626" strokeWidth={2}
              label={{ value: `Out of stock ${fmtDate(oos_datum)}`, position: "insideTopLeft", fontSize: 10, fill: "#dc2626" }} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

const LANDEN = [
  "België", "Duitsland", "Frankrijk", "Verenigd Koninkrijk", "Ierland", "Spanje",
  "Italië", "Portugal", "Polen", "Oostenrijk", "Zwitserland", "Denemarken",
  "Zweden", "Noorwegen", "Finland", "Tsjechië", "Slowakije", "Hongarije",
  "Roemenië", "Griekenland", "Luxemburg", "Kroatië", "Slovenië", "Bulgarije",
  "Estland", "Letland", "Litouwen", "Cyprus", "Malta",
  "Verenigde Staten", "Canada", "Australië", "Verenigde Arabische Emiraten",
];

function VertalingenEditor({ vertalingen, onChange }) {
  const [rows, setRows] = useState(() => metKeys(vertalingen));
  useEffect(() => { setRows(metKeys(vertalingen)); }, [vertalingen]);
  const update = (idx, key, val) => setRows(rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  const save = (next) => { setRows(next); onChange(zonderKeys(next)); };
  const add = () => save([...rows, { land: "", naam: "", _key: nieuweRijKey() }]);
  const remove = (idx) => save(rows.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3" data-testid="vertalingen-editor">
      {rows.length === 0 && (
        <p className="text-xs text-muted-foreground">Nog geen landspecifieke vertalingen. Zonder land valt facturatie terug op de Engelse naam hierboven.</p>
      )}
      {rows.map((r, idx) => (
        <div key={r._key} className="flex items-end gap-2" data-testid={`vertaling-row-${idx}`}>
          <div className="w-48 shrink-0">
            <Label className="text-xs text-muted-foreground">Land</Label>
            <Select value={r.land || ""} onValueChange={(v) => save(rows.map((x, i) => (i === idx ? { ...x, land: v } : x)))}>
              <SelectTrigger data-testid={`vertaling-land-${idx}`}><SelectValue placeholder="Kies land" /></SelectTrigger>
              <SelectContent>{LANDEN.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label className="text-xs text-muted-foreground">Artikelnaam (vertaald)</Label>
            <Input value={r.naam || ""} onChange={(e) => update(idx, "naam", e.target.value)}
              onBlur={() => onChange(zonderKeys(rows))}
              placeholder="Naam in deze taal" data-testid={`vertaling-naam-${idx}`} />
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={() => remove(idx)}
            className="text-destructive shrink-0" data-testid={`vertaling-remove-${idx}`}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="vertaling-add">
        <Plus className="h-4 w-4 mr-1" /> Vertaling toevoegen
      </Button>
    </div>
  );
}

const PT_TALEN = ["Nederlands", "Duits", "Frans", "Engels", "Spaans", "Italiaans"];
// Deze talen staan altijd aan als commerciële tekst (worden automatisch geactiveerd).
const PT_STANDAARD_TALEN = ["Nederlands", "Engels", "Frans", "Duits"];
// Zorg dat elk product minstens NL/EN/FR/DE als taalblok heeft, in vaste volgorde vooraan.
function metStandaardTalen(arr) {
  const rows = Array.isArray(arr) ? [...arr] : [];
  const heeft = (t) => rows.some((r) => (r.taal || "").trim().toLowerCase() === t.toLowerCase());
  for (const t of PT_STANDAARD_TALEN) {
    if (!heeft(t)) rows.push({ taal: t, producttekst: "", claims: "", huidtype: "", usp: "", claims_gevalideerd: false });
  }
  const rank = (t) => { const i = PT_STANDAARD_TALEN.indexOf(t); return i === -1 ? 99 : i; };
  rows.sort((a, b) => rank(a.taal) - rank(b.taal));
  return rows;
}

// Shop-vertalingen editor voor het per-taal dict-formaat vertalingen_i18n.
// Talen zijn uitbreidbaar via "Taal toevoegen".
const SHOP_TALEN_OPTIES = [
  { code: "en", label: "Engels" },
  { code: "de", label: "Duits" },
  { code: "fr", label: "Frans" },
  { code: "es", label: "Spaans" },
  { code: "it", label: "Italiaans" },
  { code: "pl", label: "Pools" },
];

function ShopVertalingenEditor({ value, fallbackNaamEn, onChange }) {
  const dict = value && typeof value === "object" ? value : {};
  const actieveTalen = Object.keys(dict);
  const beschikbaar = SHOP_TALEN_OPTIES.filter(o => !actieveTalen.includes(o.code));
  const setBlok = (taal, patch) => {
    const nieuw = { ...dict, [taal]: { ...(dict[taal] || {}), ...patch } };
    onChange(nieuw);
  };
  const verwijder = (taal) => {
    const nieuw = { ...dict };
    delete nieuw[taal];
    onChange(nieuw);
  };
  const voegToe = (taal) => {
    if (!taal) return;
    onChange({ ...dict, [taal]: { naam: taal === "en" ? (fallbackNaamEn || "") : "", omschrijving: "" } });
  };
  const label = (code) => (SHOP_TALEN_OPTIES.find(o => o.code === code) || { label: code.toUpperCase() }).label;

  return (
    <div className="space-y-3" data-testid="shop-vertalingen-editor">
      {actieveTalen.length === 0 && (
        <p className="text-xs text-muted-foreground italic">
          Nog geen vertalingen — webshop toont dan de Nederlandse tekst voor iedereen.
        </p>
      )}
      {actieveTalen.map((taal) => (
        <div key={taal} className="rounded-md border border-border p-3 space-y-2 bg-secondary/20"
             data-testid={`shop-vertaling-${taal}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide">{label(taal)} ({taal})</span>
            <Button variant="ghost" size="sm" onClick={() => verwijder(taal)} className="h-7 text-destructive"
                    data-testid={`shop-vertaling-verwijder-${taal}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Productnaam</Label>
            <Input defaultValue={(dict[taal] || {}).naam || ""}
              onBlur={(e) => { if (e.target.value !== ((dict[taal] || {}).naam || "")) setBlok(taal, { naam: e.target.value }); }}
              placeholder={`Naam in ${label(taal).toLowerCase()}`}
              data-testid={`shop-vertaling-naam-${taal}`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Omschrijving</Label>
            <Textarea defaultValue={(dict[taal] || {}).omschrijving || ""}
              onBlur={(e) => { if (e.target.value !== ((dict[taal] || {}).omschrijving || "")) setBlok(taal, { omschrijving: e.target.value }); }}
              rows={2} placeholder={`Omschrijving in ${label(taal).toLowerCase()}`}
              data-testid={`shop-vertaling-omschr-${taal}`} />
          </div>
        </div>
      ))}
      {beschikbaar.length > 0 && (
        <div className="flex items-center gap-2">
          <select onChange={(e) => { voegToe(e.target.value); e.target.value = ""; }}
            className="px-2 py-1.5 text-sm rounded-md border border-border bg-background"
            data-testid="shop-vertaling-toevoegen">
            <option value="">+ Taal toevoegen…</option>
            {beschikbaar.map((o) => (
              <option key={o.code} value={o.code}>{o.label} ({o.code})</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

function BolKoppelingenEditor({ koppelingen, innerVerpakking, outerVerpakking, onChange }) {
  const [rows, setRows] = useState(() => metKeys(koppelingen || []));
  useEffect(() => { setRows(metKeys(koppelingen || [])); }, [koppelingen]);
  const save = (next) => { setRows(next); onChange(zonderKeys(next)); };
  const update = (idx, key, val) => setRows(rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  const defaultStuks = (niveau) => niveau === "inner" ? (innerVerpakking || 1) : niveau === "outer" ? (outerVerpakking || 1) : 1;
  const add = () => save([...rows, {
    niveau: "individueel", ean: "", offer_id: "", titel: "", aantal_stuks: 1, _key: nieuweRijKey(),
  }]);
  const remove = (idx) => save(rows.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3" data-testid="bol-koppelingen-editor">
      {rows.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Nog geen bol.com-koppelingen. Voeg één rij toe per verpakkingsniveau dat je op bol verkoopt (bv. inner-pack van 6 stuks).
        </p>
      )}
      {rows.map((r, idx) => (
        <div key={r._key} className="grid grid-cols-12 gap-2 items-end p-2 rounded-lg border border-border/60 bg-secondary/30"
          data-testid={`bol-kop-row-${idx}`}>
          <div className="col-span-6 sm:col-span-2">
            <Label className="text-xs text-muted-foreground">Niveau</Label>
            <Select value={r.niveau || "individueel"}
              onValueChange={(v) => {
                const next = rows.map((x, i) => (i === idx
                  ? { ...x, niveau: v, aantal_stuks: (x.aantal_stuks && x.aantal_stuks !== 1) ? x.aantal_stuks : defaultStuks(v) }
                  : x));
                save(next);
              }}>
              <SelectTrigger data-testid={`bol-niveau-${idx}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="individueel">Individueel (1 stuk)</SelectItem>
                <SelectItem value="inner">Inner-pack{innerVerpakking ? ` (${innerVerpakking})` : ""}</SelectItem>
                <SelectItem value="outer">Outer-pack{outerVerpakking ? ` (${outerVerpakking})` : ""}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Label className="text-xs text-muted-foreground">Bol EAN</Label>
            <Input value={r.ean || ""} onChange={(e) => update(idx, "ean", e.target.value)}
              onBlur={() => onChange(zonderKeys(rows))}
              placeholder="8719...." inputMode="numeric" className="font-mono"
              data-testid={`bol-ean-${idx}`} />
          </div>
          <div className="col-span-6 sm:col-span-2">
            <Label className="text-xs text-muted-foreground">Offer-ID (opt.)</Label>
            <Input value={r.offer_id || ""} onChange={(e) => update(idx, "offer_id", e.target.value)}
              onBlur={() => onChange(zonderKeys(rows))}
              placeholder="bsku / offer-id" className="font-mono text-xs"
              data-testid={`bol-offer-${idx}`} />
          </div>
          <div className="col-span-4 sm:col-span-2">
            <Label className="text-xs text-muted-foreground">Stuks per pack</Label>
            <Input type="number" min="1" step="1" value={r.aantal_stuks || 1}
              onChange={(e) => update(idx, "aantal_stuks", parseInt(e.target.value || "1", 10))}
              onBlur={() => onChange(zonderKeys(rows))}
              className="text-right"
              data-testid={`bol-stuks-${idx}`} />
          </div>
          <div className="col-span-8 sm:col-span-3">
            <Label className="text-xs text-muted-foreground">Titel op bol (opt.)</Label>
            <Input value={r.titel || ""} onChange={(e) => update(idx, "titel", e.target.value)}
              onBlur={() => onChange(zonderKeys(rows))}
              placeholder="Bol-productnaam" data-testid={`bol-titel-${idx}`} />
          </div>
          <div className="col-span-12 sm:col-span-12 flex justify-end sm:absolute sm:mt-6">
            <Button type="button" variant="ghost" size="icon" onClick={() => remove(idx)}
              className="text-destructive h-8 w-8" data-testid={`bol-remove-${idx}`}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="bol-kop-add">
        <Plus className="h-4 w-4 mr-1" /> Bol.com-koppeling toevoegen
      </Button>
    </div>
  );
}

function Productteksten({ value, onChange, productId, veld = "productteksten" }) {
  const seed = () => metKeys(metStandaardTalen(value));
  const [rows, setRows] = useState(seed);
  const [vertalen, setVertalen] = useState(null); // idx dat op dit moment vertaald wordt
  const [briefing, setBriefing] = useState("");
  const [schrijven, setSchrijven] = useState(false);
  const schrijfAlles = async () => {
    if (!productId) { toast.error("Sla het product eerst op"); return; }
    setSchrijven(true);
    try {
      const { data } = await api.post(`/producten/${productId}/productteksten/genereer`,
        { talen: PT_STANDAARD_TALEN, briefing, veld });
      save(metKeys(metStandaardTalen(data.teksten || data.productteksten || [])));
      toast.success("Teksten geschreven door Claude — controleer de claims");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI-schrijven mislukt"); }
    finally { setSchrijven(false); }
  };
  useEffect(() => {
    setRows(metKeys(metStandaardTalen(value)));
  }, [value]);

  const update = (idx, key, val) => setRows(rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  const save = (next) => { setRows(next); onChange(zonderKeys(next)); };
  const used = rows.map((r) => r.taal);
  const beschikbaar = PT_TALEN.filter((t) => !used.includes(t));
  const add = () => save([...rows, { taal: beschikbaar[0] || "", producttekst: "", claims: "", huidtype: "", usp: "", claims_gevalideerd: false, _key: nieuweRijKey() }]);
  const remove = (idx) => save(rows.filter((_, i) => i !== idx));

  const isStandaardTaal = (t) => PT_STANDAARD_TALEN.some((x) => x.toLowerCase() === (t || "").trim().toLowerCase());

  const vertaalRij = async (idx) => {
    const doeltaal = (rows[idx]?.taal || "").trim();
    if (!doeltaal) { toast.error("Kies eerst een taal voor deze rij"); return; }
    if (!productId) { toast.error("Sla het product eerst op"); return; }
    const nl = rows.find((r) => (r.taal || "").toLowerCase() === "nederlands") || rows[0];
    if (!nl?.producttekst?.trim()) {
      toast.error("Vul eerst de Nederlandse producttekst in"); return;
    }
    setVertalen(idx);
    try {
      const { data } = await api.post(`/producten/${productId}/productteksten/vertaal`,
        { bron_taal: "Nederlands", doel_talen: [doeltaal], veld });
      // Merge server-antwoord terug in local state
      const nieuw = data.teksten || data.productteksten || [];
      const nieuwMetKeys = metKeys(metStandaardTalen(nieuw));
      save(nieuwMetKeys);
      toast.success(`Vertaald naar ${doeltaal}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vertaling mislukt");
    } finally { setVertalen(null); }
  };

  return (
    <div className="space-y-4" data-testid="productteksten-editor">
      <div className="text-xs text-muted-foreground bg-secondary/40 border border-border rounded-lg p-3 space-y-2">
        <div className="flex items-start gap-2">
          <Sparkles className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
          <span>NL, EN, FR en DE staan altijd aan als commerciële tekst. Je eigen Claude (via de connector) of de
            knop hieronder kan deze teksten per taal schrijven. <strong>Claims worden altijd als “te valideren” opgeslagen</strong> —
            vink per taal “Claims gevalideerd” aan zodra je ze hebt gecontroleerd.</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Input value={briefing} onChange={(e) => setBriefing(e.target.value)}
            placeholder="Optionele briefing voor Claude (bijv. doelgroep, toon, kernvoordeel)…"
            className="h-9 text-sm flex-1" data-testid="pt-briefing" />
          <Button type="button" size="sm" onClick={schrijfAlles} disabled={schrijven}
            data-testid="pt-schrijf-ai" className="shrink-0">
            {schrijven ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            Schrijf met AI (NL/EN/FR/DE)
          </Button>
        </div>
      </div>
      {rows.map((r, idx) => {
        const isNL = (r.taal || "").trim().toLowerCase() === "nederlands";
        const vast = isStandaardTaal(r.taal);
        return (
        <div key={r._key} className="border border-border rounded-lg p-4 space-y-3" data-testid={`pt-row-${idx}`}>
          <div className="flex items-center justify-between gap-2">
            {vast ? (
              <div className="text-sm font-medium flex items-center gap-2">
                <Languages className="h-4 w-4 text-primary" /> {r.taal}
                {isNL && <span className="text-xs text-muted-foreground font-normal">(standaard)</span>}
              </div>
            ) : (
              <div className="w-56">
                <Select value={r.taal || ""} onValueChange={(v) => save(rows.map((x, i) => (i === idx ? { ...x, taal: v } : x)))}>
                  <SelectTrigger data-testid={`pt-taal-${idx}`}><SelectValue placeholder="Kies taal" /></SelectTrigger>
                  <SelectContent>
                    {[r.taal, ...beschikbaar].filter(Boolean).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center gap-1">
              {!isNL && (
                <Button type="button" variant="outline" size="sm" onClick={() => vertaalRij(idx)}
                  disabled={vertalen === idx} data-testid={`pt-vertaal-${idx}`}
                  className="h-8 text-xs">
                  {vertalen === idx
                    ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                  AI-vertaal vanuit NL
                </Button>
              )}
              {!vast && (
                <Button type="button" variant="ghost" size="icon" onClick={() => remove(idx)}
                  className="text-destructive shrink-0" data-testid={`pt-remove-${idx}`}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Korte omschrijving (productkaart webshop)</Label>
            <Input value={r.korte_omschrijving || ""}
              placeholder="Korte verkoopzin voor de productkaart"
              onChange={(e) => update(idx, "korte_omschrijving", e.target.value)}
              onBlur={() => onChange(zonderKeys(rows))} data-testid={`pt-korte-${idx}`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Producttekst</Label>
            <Textarea value={r.producttekst || ""} rows={4}
              onChange={(e) => update(idx, "producttekst", e.target.value)}
              onBlur={() => onChange(zonderKeys(rows))} data-testid={`pt-producttekst-${idx}`} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Claims</Label>
              <Input value={r.claims || ""}
                onChange={(e) => setRows(rows.map((x, i) => (i === idx ? { ...x, claims: e.target.value, claims_gevalideerd: false } : x)))}
                onBlur={() => onChange(zonderKeys(rows))} data-testid={`pt-claims-${idx}`} />
              <div className="flex items-center gap-2 mt-1.5">
                <Checkbox id={`pt-claims-val-${idx}`} checked={!!r.claims_gevalideerd}
                  onCheckedChange={(v) => save(rows.map((x, i) => (i === idx ? { ...x, claims_gevalideerd: !!v } : x)))}
                  data-testid={`pt-claims-gevalideerd-${idx}`} />
                <label htmlFor={`pt-claims-val-${idx}`} className="text-xs text-muted-foreground cursor-pointer">Claims gevalideerd</label>
                {(r.claims || "").trim() && (r.claims_gevalideerd
                  ? <Badge variant="outline" className="text-[10px] border-green-600 text-green-700" data-testid={`pt-claims-badge-${idx}`}>Gevalideerd</Badge>
                  : <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-600" data-testid={`pt-claims-badge-${idx}`}>Te valideren</Badge>)}
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Huidtype</Label>
              <Input value={r.huidtype || ""}
                onChange={(e) => update(idx, "huidtype", e.target.value)}
                onBlur={() => onChange(zonderKeys(rows))} data-testid={`pt-huidtype-${idx}`} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-muted-foreground">USP (max. 90 tekens, voor het winkelschap)</Label>
              <Input value={r.usp || ""} maxLength={90}
                placeholder="Bijv. Verzorgt en matteert de gecombineerde huid"
                onChange={(e) => update(idx, "usp", e.target.value)}
                onBlur={() => onChange(zonderKeys(rows))} data-testid={`pt-usp-${idx}`} />
            </div>
          </div>
        </div>
        );
      })}
      {beschikbaar.length > 0 && (
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="pt-add-taal">
          <Plus className="h-4 w-4 mr-1" /> Taal toevoegen
        </Button>
      )}
    </div>
  );
}


function BatchRegistratie({ value, onChange, voorraadTotaal, resterend = [] }) {
  const [rows, setRows] = useState(() => metKeys(value));
  useEffect(() => { setRows(metKeys(value)); }, [value]);
  const update = (idx, key, val) => setRows(rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  const save = (next) => { setRows(next); onChange(zonderKeys(next)); };
  const add = () => save([...rows, { datum: "", batchnummer: "", traceerbaarheid: "", voorraad: "", _key: nieuweRijKey() }]);
  const remove = (idx) => save(rows.filter((_, i) => i !== idx));

  // Actuele traceerbaarheid = meest recente batch (op datum; bij lege datum de laatst toegevoegde)
  const actueel = (() => {
    if (!rows.length) return "";
    const sorted = [...rows].sort((a, b) => (a.datum || "").localeCompare(b.datum || ""));
    return (sorted[sorted.length - 1].traceerbaarheid || "").trim();
  })();

  // Restvoorraad wordt door de backend (FIFO) berekend en per batch teruggegeven in `resterend`.
  const somResterend = (resterend || []).reduce((s, v) => s + (Number(v) || 0), 0);
  const heeftBatches = rows.some((r) => String(r.voorraad ?? "").trim() !== "" || r.batchnummer || r.traceerbaarheid);
  const klopt = voorraadTotaal == null || Math.abs(somResterend - voorraadTotaal) < 0.5;

  return (
    <div className="space-y-3" data-testid="batch-registratie">
      <div className="text-xs text-muted-foreground">
        Registreer per batch: datum, batchnummer, traceerbaarheid (bijv. COSMOS NATURAL-nummer) en het <strong className="text-foreground">ontvangen aantal</strong> van die batch.
        De <strong className="text-foreground">restvoorraad wordt automatisch berekend (FIFO)</strong>: verkopen, verliezen en samples gaan van de oudste batch af — je hoeft dit niet handmatig bij te werken.
        Alleen de traceerbaarheid van de meest recente batch verschijnt op de pakbon en factuur.
      </div>
      {actueel && (
        <div className="inline-flex items-center gap-2 rounded-md bg-primary/10 px-3 py-1.5 text-sm" data-testid="batch-actueel">
          <span className="text-muted-foreground">Actueel op documenten:</span>
          <span className="font-medium">{actueel}</span>
        </div>
      )}
      {rows.map((r, idx) => (
        <div key={r._key} className="flex items-end gap-2" data-testid={`batch-row-${idx}`}>
          <div className="w-32 shrink-0">
            <Label className="text-xs text-muted-foreground">Datum</Label>
            <Input type="date" value={(r.datum || "").slice(0, 10)}
              onChange={(e) => update(idx, "datum", e.target.value)}
              onBlur={() => onChange(zonderKeys(rows))} data-testid={`batch-datum-${idx}`} />
          </div>
          <div className="w-32 shrink-0">
            <Label className="text-xs text-muted-foreground">Batchnummer</Label>
            <Input value={r.batchnummer || ""}
              onChange={(e) => update(idx, "batchnummer", e.target.value)}
              onBlur={() => onChange(zonderKeys(rows))} data-testid={`batch-nummer-${idx}`} />
          </div>
          <div className="flex-1 min-w-[120px]">
            <Label className="text-xs text-muted-foreground">Traceerbaarheid</Label>
            <Input value={r.traceerbaarheid || ""}
              placeholder="Bijv. 12345 COSMOS NATURAL"
              onChange={(e) => update(idx, "traceerbaarheid", e.target.value)}
              onBlur={() => onChange(zonderKeys(rows))} data-testid={`batch-trace-${idx}`} />
          </div>
          <div className="w-24 shrink-0">
            <Label className="text-xs text-muted-foreground">Ontvangen</Label>
            <Input type="number" inputMode="numeric" value={r.voorraad ?? ""}
              placeholder="0" className="text-right"
              onChange={(e) => update(idx, "voorraad", e.target.value)}
              onBlur={() => onChange(zonderKeys(rows))} data-testid={`batch-ontvangen-${idx}`} />
          </div>
          <div className="w-20 shrink-0">
            <Label className="text-xs text-muted-foreground">Rest (FIFO)</Label>
            <div className="h-9 flex items-center justify-end px-2 rounded-md bg-secondary/60 text-sm font-medium tabular-nums"
              data-testid={`batch-rest-${idx}`}>
              {resterend[idx] != null ? resterend[idx] : "—"}
            </div>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={() => remove(idx)}
            className="text-destructive shrink-0" data-testid={`batch-remove-${idx}`}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      {heeftBatches && (resterend || []).length > 0 && (
        <div className={`text-xs rounded-md px-3 py-1.5 inline-flex items-center gap-2 ${klopt ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="batch-voorraad-check">
          <span>Som resterende batchvoorraad: <strong>{somResterend}</strong></span>
          {voorraadTotaal != null && (
            <span>· actuele eigen voorraad: <strong>{voorraadTotaal}</strong>{klopt ? " ✓" : ` · verschil ${(somResterend - voorraadTotaal).toFixed(0)}`}</span>
          )}
        </div>
      )}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="batch-add">
        <Plus className="h-4 w-4 mr-1" /> Batch toevoegen
      </Button>
    </div>
  );
}


function ProductPicker({ value, naam, onPick }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open || term.trim().length < 1) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/producten", { params: { q: term.trim() } });
        setResults((data || []).slice(0, 8));
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [term, open]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const display = value ? `${naam || ""} (${value})`.trim() : "";

  return (
    <div className="relative" ref={boxRef}>
      <Input
        value={open ? term : display}
        placeholder="Zoek op productnaam of SKU…"
        onFocus={() => { setOpen(true); setTerm(""); }}
        onChange={(e) => { setOpen(true); setTerm(e.target.value); }}
        data-testid="component-picker-input"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
          {results.map((r) => (
            <button key={r.id} type="button"
              className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex justify-between gap-2 items-center"
              onClick={() => { onPick(r); setOpen(false); setTerm(""); }}
              data-testid={`component-suggestion-${r.sku}`}>
              <span className="truncate">{r.naam}</span>
              <span className="text-xs text-muted-foreground font-mono shrink-0">{r.sku}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SamengesteldEditor({ componenten, onChange }) {
  const [rows, setRows] = useState(() => metKeys(componenten));
  useEffect(() => { setRows(metKeys(componenten)); }, [componenten]);
  const update = (idx, key, val) => setRows(rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  const save = (next) => { setRows(next); onChange(zonderKeys(next)); };
  const add = () => save([...rows, { sku: "", aantal: 1, _key: nieuweRijKey() }]);
  const remove = (idx) => save(rows.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3" data-testid="componenten-editor">
      {rows.length === 0 && (
        <p className="text-xs text-muted-foreground">Nog geen componenten. Voeg de artikelnummers (SKU) en het aantal per stuk toe.</p>
      )}
      {rows.map((r, idx) => (
        <div key={r._key} className="flex items-end gap-2" data-testid={`component-row-${idx}`}>
          <div className="flex-1">
            <Label className="text-xs text-muted-foreground">Product</Label>
            <ProductPicker value={r.sku} naam={r.naam}
              onPick={(prod) => save(rows.map((x, i) => (i === idx ? { ...x, sku: prod.sku, naam: prod.naam } : x)))} />
          </div>
          <div className="w-28 shrink-0">
            <Label className="text-xs text-muted-foreground">Aantal</Label>
            <Input type="number" min="1" value={r.aantal ?? 1}
              onChange={(e) => update(idx, "aantal", parseInt(e.target.value, 10) || 0)}
              onBlur={() => onChange(zonderKeys(rows))} data-testid={`component-aantal-${idx}`} />
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={() => remove(idx)}
            className="text-destructive shrink-0" data-testid={`component-remove-${idx}`}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="component-add">
        <Plus className="h-4 w-4 mr-1" /> Component toevoegen
      </Button>
    </div>
  );
}


function VoorraadCorrectie({ productId, onDone }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("correctie");
  const [deel, setDeel] = useState("warehouse");
  const [aantal, setAantal] = useState("");
  const [referentie, setReferentie] = useState("");
  const [notitie, setNotitie] = useState("");

  const submit = async () => {
    const n = parseInt(aantal, 10);
    if (isNaN(n) || n === 0) { toast.error("Vul een aantal (mag negatief)"); return; }
    await api.post("/voorraadmutaties", { product_id: productId, type, aantal: n, voorraad_deel: deel, referentie, notitie });
    toast.success("Mutatie opgeslagen"); setOpen(false);
    setAantal(""); setReferentie(""); setNotitie("");
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="mutatie-btn">Voorraad corrigeren</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Voorraadmutatie</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Voorraaddeel</Label>
            <Select value={deel} onValueChange={setDeel}>
              <SelectTrigger data-testid="mut-deel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="warehouse">Eigen warehouse</SelectItem>
                <SelectItem value="leverancier">Leverancier</SelectItem>
                <SelectItem value="china">Order fabrikant (China)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="mut-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["correctie","inkoop","verkoop"].map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Aantal {type === "verkoop" ? "(positief — wordt afgeboekt)" : type === "correctie" ? "(positief = bij, negatief = af)" : ""}</Label>
            <Input type="number" data-testid="mut-aantal" value={aantal} onChange={(e)=>setAantal(e.target.value)} />
          </div>
          <div><Label>Referentie</Label><Input value={referentie} onChange={(e)=>setReferentie(e.target.value)} placeholder="Bijv. orderbon, factuurnr" /></div>
          <div><Label>Notitie</Label><Textarea value={notitie} onChange={(e)=>setNotitie(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="mut-submit">Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Beveiligde correctie-popup voor één specifiek voorraaddeel (warehouse of
 * leverancier). Werkt met + / − knoppen én een directe "Nieuwe waarde"-input,
 * zodat er niet meer per ongeluk op het kaart-veld getikt kan worden.
 * De correctie wordt intern als delta (nieuw − oud) doorgezet als
 * `type=correctie` op `/voorraadmutaties`.
 */

function GrootsteAfnemers({ productId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/producten/${productId}/grootste-afnemers?maanden=12&limit=5`)
      .then(({ data }) => { if (alive) setRows(data || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [productId]);
  if (rows === null) return <div className="text-sm text-muted-foreground">Afnemers laden…</div>;
  if (rows.length === 0) return <div className="text-sm text-muted-foreground">Nog geen verkopen in de laatste 12 maanden.</div>;
  return (
    <div className="divide-y divide-border border border-border rounded-lg" data-testid="grootste-afnemers">
      {rows.map((r, i) => (
        <div key={r.organisatie_id} className="flex items-center gap-3 px-3 py-2 text-sm"
             data-testid={`afnemer-rij-${i}`}>
          <div className="w-6 h-6 rounded-full bg-secondary text-xs flex items-center justify-center font-medium">{i+1}</div>
          <Link to={`/organisaties/${r.organisatie_id}`}
                className="flex-1 text-primary hover:underline truncate">{r.naam}</Link>
          <div className="tabular-nums font-medium">{r.aantal}</div>
          <div className="text-xs text-muted-foreground">stuks</div>
        </div>
      ))}
    </div>
  );
}

function LedgerKeuze({ value, onChange, testId = "prod-moneybird-ledger" }) {
  const [opts, setOpts] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/moneybird/ledger-accounts/actief")
      .then(({ data }) => { if (alive) setOpts(data || []); })
      .catch(() => { if (alive) setOpts([]); });
    return () => { alive = false; };
  }, []);
  if (opts === null) return <div className="text-sm text-muted-foreground">Grootboeken laden…</div>;
  return (
    <Select value={value || "geen"} onValueChange={(v) => onChange(v === "geen" ? null : v)}>
      <SelectTrigger data-testid={testId}>
        <SelectValue placeholder="— default (Omzet) —" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="geen">— default (Omzet — standaard) —</SelectItem>
        {opts.map(l => (
          <SelectItem key={l.id} value={l.id}>
            {l.code ? <span className="font-mono text-xs mr-2">{l.code}</span> : null}
            {l.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function VoorraadDeelCorrectie({ productId, deel, deelLabel, huidig, onDone }) {
  const [open, setOpen] = useState(false);
  const [nieuw, setNieuw] = useState(0);
  const [notitie, setNotitie] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setNieuw(Number(huidig) || 0); setNotitie(""); } }, [open, huidig]);

  const delta = Number(nieuw) - Number(huidig || 0);
  const submit = async () => {
    if (delta === 0) { toast.error("Nieuwe waarde is gelijk aan huidige"); return; }
    setSaving(true);
    try {
      await api.post("/voorraadmutaties", {
        product_id: productId, type: "correctie", aantal: delta,
        voorraad_deel: deel, referentie: `handmatige correctie ${deelLabel}`,
        notitie,
      });
      toast.success(`${deelLabel}: ${huidig} → ${nieuw} (${delta > 0 ? "+" : ""}${delta})`);
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Correctie mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`voorraad-corrigeer-${deel}`}
                title={`Corrigeer ${deelLabel.toLowerCase()}`}>
          Corrigeer
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Voorraad corrigeren — {deelLabel}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-secondary/40 border border-border p-4 text-center">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Huidige voorraad</div>
            <div className="font-display text-3xl font-medium tabular-nums" data-testid={`voorraad-huidig-${deel}`}>{huidig ?? 0}</div>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Nieuwe waarde</Label>
            <div className="flex items-center gap-2 mt-1">
              <Button variant="outline" size="icon" className="h-10 w-10"
                      onClick={()=>setNieuw((n)=>Math.max(0, Number(n||0) - 1))}
                      data-testid={`voorraad-min-${deel}`}>−</Button>
              <Input type="number" value={nieuw} step="1"
                     onChange={(e)=>setNieuw(e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0)}
                     className="h-10 text-center text-lg tabular-nums font-medium"
                     data-testid={`voorraad-nieuw-${deel}`} />
              <Button variant="outline" size="icon" className="h-10 w-10"
                      onClick={()=>setNieuw((n)=>Number(n||0) + 1)}
                      data-testid={`voorraad-plus-${deel}`}>+</Button>
            </div>
            <div className={`text-xs mt-2 ${delta === 0 ? "text-muted-foreground" : delta > 0 ? "text-emerald-700" : "text-amber-700"}`}>
              Verschil: {delta > 0 ? "+" : ""}{delta} {delta === 0 ? "(geen wijziging)" : delta > 0 ? "(bij)" : "(af)"}
            </div>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Notitie (optioneel)</Label>
            <Textarea rows={2} value={notitie} onChange={(e)=>setNotitie(e.target.value)}
                      placeholder="Bijv. handmatige telling na inventarisatie…"
                      data-testid={`voorraad-notitie-${deel}`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)} disabled={saving}>Annuleren</Button>
          <Button onClick={submit} disabled={saving || delta === 0} data-testid={`voorraad-opslaan-${deel}`}>
            {saving ? "Opslaan…" : "Bevestig correctie"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const nav = useDetailNav("producten", id, "/producten");
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const [p, setP] = useState(null);
  const [muts, setMuts] = useState([]);
  const [mutTotaal, setMutTotaal] = useState(0);
  const [mutPagina, setMutPagina] = useState(0);
  const [peilingen, setPeilingen] = useState([]);
  const [peilTotaal, setPeilTotaal] = useState(0);
  const [peilPagina, setPeilPagina] = useState(0);
  const [historieWeken, setHistorieWeken] = useState(8);
  const [modus, setModus] = useState("bestel");
  const kiesModus = (m) => {
    setModus(m);
    setHistorieWeken(m === "bestel" ? 8 : m === "oos" ? 4 : null);
  };
  const [delOpen, setDelOpen] = useState(false);
  const [webshopCategorieen, setWebshopCategorieen] = useState([]);
  const [merken, setMerken] = useState([]);

  useEffect(() => {
    api.get("/producten/webshop-categorieen").then((r) => setWebshopCategorieen((r.data || []).map(x => x.naam))).catch(() => {});
    api.get("/producten/merken").then((r) => setMerken((r.data || []).map(x => x.merk))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const [{ data: prod }, { data: m }] = await Promise.all([
      api.get(`/producten/${id}`, { params: historieWeken ? { historie_weken: historieWeken } : {} }),
      api.get("/voorraadmutaties", { params: { product_id: id, skip: mutPagina * 20, limit: 20 }}),
    ]);
    setP(prod); setMuts(m.items || []); setMutTotaal(m.totaal || 0);
  }, [id, mutPagina, historieWeken]);
  useEffect(() => { load(); }, [load]);
  // Bij prev/next-navigatie (zelfde component, andere id): reset zodat de
  // uncontrolled velden opnieuw mounten met de nieuwe productdata.
  useEffect(() => { setP(null); setMuts([]); setMutPagina(0); }, [id]);

  useEffect(() => {
    api.get("/voorraad/peilingen", { params: { product_id: id, skip: peilPagina * 20, limit: 20 }})
      .then((r) => { setPeilingen(r.data?.items || []); setPeilTotaal(r.data?.totaal || 0); })
      .catch(() => {});
  }, [id, peilPagina]);

  const patch = async (field, value) => {
    try {
      const { data } = await api.patch(`/producten/${id}`, { [field]: value });
      setP(data); toast.success("Opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const remove = async () => {
    try {
      await api.delete(`/producten/${id}`);
      toast.success("Verwijderd");
      window.history.back();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  if (!p) return <div className="text-muted-foreground">Laden…</div>;

  const numField = (label, field, step = "0.01") => (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input type="number" step={step} defaultValue={p[field] ?? 0}
        onBlur={(e)=>{
          const num = parseFloat(String(e.target.value).replace(",", ".")) || 0;
          if (num !== Number(p[field] ?? 0)) patch(field, num);
        }}
        data-testid={`prod-${field}`} />
    </div>
  );
  const txtField = (label, field, placeholder = "") => (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input defaultValue={p[field] || ""}
        placeholder={placeholder}
        onBlur={(e)=>{ if (e.target.value !== (p[field] || "")) patch(field, e.target.value); }}
        data-testid={`prod-${field}`} />
    </div>
  );
  const intField = (label, field) => (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input type="number" step="1" defaultValue={p[field] ?? 0}
        onBlur={(e)=>{ const num = parseInt(e.target.value, 10) || 0; if (num !== Number(p[field] ?? 0)) patch(field, num); }}
        data-testid={`prod-${field}`} />
    </div>
  );
  const dateField = (label, field) => (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input type="date" defaultValue={(p[field] || "").slice(0, 10)}
        onBlur={(e)=>{ const v = e.target.value ? new Date(e.target.value).toISOString() : ""; if (v !== (p[field] || "")) patch(field, v); }}
        data-testid={`prod-${field}`} />
    </div>
  );
  const weekField = (label, field) => (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input type="number" min="1" max="53" step="1" defaultValue={p[field] ?? ""} placeholder="wk"
        onBlur={(e)=>{ const num = e.target.value ? (parseInt(e.target.value, 10) || null) : null; if (num !== (p[field] ?? null)) patch(field, num); }}
        data-testid={`prod-${field}`} />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="product-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(nav.terug || "/producten")} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Overzicht
          </button>
          <OverzichtNav nav={nav} basePad="/producten" testid="product-nav" />
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => patch("gearchiveerd", !p.gearchiveerd)}
              data-testid="prod-archive-toggle">
              {p.gearchiveerd ? <><ArchiveRestore className="h-4 w-4 mr-1" />Herstellen</> : <><Archive className="h-4 w-4 mr-1" />Archiveren</>}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDelOpen(true)} className="text-destructive hover:text-destructive" data-testid="prod-delete">
              <Trash2 className="h-4 w-4 mr-1" />Verwijderen
            </Button>
          </div>
        )}
      </div>

      {p.gearchiveerd && (
        <div className="bg-secondary/40 border border-border rounded-lg px-4 py-2.5 text-sm text-muted-foreground" data-testid="prod-archief-banner">
          Dit product is <strong className="text-foreground">gearchiveerd</strong>: het telt nog mee in de metrics, maar is niet selecteerbaar in verkoopkansen.
        </div>
      )}

      <ConfirmDeleteDialog
        open={delOpen}
        onOpenChange={setDelOpen}
        title="Product verwijderen?"
        description={`Je staat op het punt "${p.naam}" (${p.sku}) definitief te verwijderen. Dit kan niet ongedaan worden gemaakt.`}
        onConfirm={remove}
        testid="prod-delete-dialog"
      />

      {/* Identiteit */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-lg bg-secondary flex items-center justify-center shrink-0"><Package className="h-6 w-6 text-primary" /></div>
          <div className="flex-1 min-w-0">
            <Input defaultValue={p.sku}
              onBlur={(e)=>{ if (e.target.value !== p.sku) patch("sku", e.target.value); }}
              data-testid="prod-sku"
              className="font-mono text-xs h-7 px-2 py-0 border-0 focus-visible:ring-1 focus-visible:ring-ring" />
            <Input defaultValue={p.naam}
              onBlur={(e)=>{ if (e.target.value !== p.naam) patch("naam", e.target.value); }}
              data-testid="prod-naam"
              className="font-display text-2xl font-medium leading-tight border-0 px-0 h-auto py-1 focus-visible:ring-0 focus-visible:border-0" />
          </div>
        </div>
      </div>

      <Tabs defaultValue="algemeen" className="w-full">
        <TabsList data-testid="prod-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="algemeen" data-testid="prod-tab-algemeen">Algemeen</TabsTrigger>
          <TabsTrigger value="voorraad" data-testid="prod-tab-voorraad">Voorraad en inkoop</TabsTrigger>
          <TabsTrigger value="financieel" data-testid="prod-tab-financieel">Financieel en beschikbaarheid</TabsTrigger>
          <TabsTrigger value="platforms" data-testid="prod-tab-platforms">Verkoopplatforms</TabsTrigger>
          <TabsTrigger value="logistiek" data-testid="prod-tab-logistiek">Logistieke data</TabsTrigger>
          <TabsTrigger value="verkoopdata" data-testid="prod-tab-verkoopdata">Commerciële data</TabsTrigger>
          <TabsTrigger value="fotos" data-testid="prod-tab-fotos">Foto's</TabsTrigger>
        </TabsList>

        {/* 1. ALGEMEEN — kern-info + grootste afnemers */}
        <TabsContent value="algemeen" className="mt-4 space-y-6">
          <div className="bg-card border border-border rounded-xl p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {txtField("EAN", "ean", "Bijv. 8710001234567")}
              <div>
                <Label className="text-xs text-muted-foreground">Merk</Label>
                <Input list="merken-list" defaultValue={p.merk || ""} placeholder="Bijv. NextBrush"
                  onBlur={(e) => { const v = e.target.value.trim(); if (v !== (p.merk || "")) patch("merk", v); }}
                  data-testid="prod-merk" />
                <datalist id="merken-list">
                  {merken.map((m) => <option key={m} value={m} />)}
                </datalist>
              </div>
              {txtField("Productgroep", "productgroep")}
              <div>
                <Label className="text-xs text-muted-foreground">Categorie (dashboard)</Label>
                <Select value={p.categorie || "geen"} onValueChange={(v)=>patch("categorie", v === "geen" ? "" : v)}>
                  <SelectTrigger data-testid="prod-categorie"><SelectValue placeholder="Geen categorie" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="geen">Geen categorie</SelectItem>
                    {["tandenborstels","ragers","Gezichtreinigers","Gezichtscrèmes","custom producten"].map(c=>(
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Eenheid</Label>
                <Select value={p.eenheid || "stuk"} onValueChange={(v)=>patch("eenheid", v)}>
                  <SelectTrigger data-testid="prod-eenheid"><SelectValue /></SelectTrigger>
                  <SelectContent>{["stuk","doos","kg","liter"].map(e=><SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="pt-4 border-t border-border">
              <MoederKoppeling ownSku={p.sku} value={p.moeder_sku || ""} onChange={(v) => patch("moeder_sku", v)} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Omschrijving</Label>
              <Textarea defaultValue={p.omschrijving || ""}
                onBlur={(e)=>{ if (e.target.value !== (p.omschrijving || "")) patch("omschrijving", e.target.value); }}
                rows={3} data-testid="prod-omschrijving" />
            </div>
            <div className="pt-4 border-t border-border">
              <div className="text-sm font-medium mb-2">Traceerbaarheid (batchregistratie)</div>
              <BatchRegistratie value={p.batches} onChange={(next) => patch("batches", next)}
                resterend={p.batches_resterend}
                voorraadTotaal={(p.voorraad_warehouse || 0) + (p.voorraad_leverancier || 0)} />
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-6 space-y-3">
            <h3 className="font-display text-xl font-medium">Grootste afnemers (laatste 12 mnd)</h3>
            <p className="text-xs text-muted-foreground">Top-5 klanten op basis van verkocht aantal — via pakbonnen &amp; verkoopkansen.</p>
            <GrootsteAfnemers productId={id} />
          </div>

          <div>
            <h3 className="font-display text-xl font-medium mb-3">Klantspecifieke codes</h3>
            <ProductKlantcodes productId={id} />
          </div>
        </TabsContent>

        {/* 2. VOORRAAD EN INKOOP */}
        <TabsContent value="voorraad" className="mt-4 space-y-6">
          <div className="bg-card border border-border rounded-xl p-6 space-y-5">
            <div className="text-sm font-medium">Inkoop &amp; leverancier</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {txtField("Leverancier", "leverancier", "Naam van de leverancier")}
              {txtField("Artikelcode leverancier", "artikelcode_leverancier", "Code zoals leverancier hem voert")}
              <div>
                <Label className="text-xs text-muted-foreground">Bestellen</Label>
                <div className="flex gap-2 mt-1">
                  <Button type="button" size="sm" variant={p.bestellen ? "default" : "outline"}
                    onClick={()=>{ if (!p.bestellen) patch("bestellen", true); }} data-testid="prod-bestellen-ja">Ja</Button>
                  <Button type="button" size="sm" variant={!p.bestellen ? "default" : "outline"}
                    onClick={()=>{ if (p.bestellen) patch("bestellen", false); }} data-testid="prod-bestellen-nee">Nee</Button>
                </div>
              </div>
              {intField("MOQ (min. bestelhoeveelheid inkoop)", "moq")}
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-6 space-y-3" data-testid="voorraad-blok">
            <div className="flex items-end justify-between gap-2">
              <div>
                <div className="text-xs text-muted-foreground">Courante voorraad</div>
                <div className={`font-display text-3xl font-medium ${p.onder_minimum ? "text-destructive" : ""}`} data-testid="prod-voorraad-courant">{p.voorraad_courant ?? 0}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                {p.onder_minimum && <Badge variant="destructive" data-testid="prod-onder-min"><AlertTriangle className="h-3 w-3 mr-1" />Onder minimum</Badge>}
                {!p.onder_minimum && p.besteladvies && <Badge className="bg-accent text-accent-foreground" data-testid="prod-besteladvies">Bestel nu</Badge>}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div data-testid="voorraad-warehouse-cell">
                <Label className="text-xs text-muted-foreground">
                  {p.moeder_sku ? "Stickers warehouse" : "Voorraad warehouse"}
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-9 px-3 rounded-md border border-border bg-secondary/30 flex items-center font-medium tabular-nums text-lg" data-testid="prod-voorraad_warehouse">
                    {p.voorraad_warehouse ?? 0}
                  </div>
                  <VoorraadDeelCorrectie productId={id} deel="warehouse" deelLabel="Warehouse"
                                          huidig={p.voorraad_warehouse ?? 0} onDone={load} />
                </div>
                {p.moeder_sku && p.moeder_voorraad_warehouse != null && (
                  <div className="text-xs mt-1.5 text-muted-foreground">
                    Verkoopbaar: <span className="font-medium tabular-nums text-foreground" data-testid="prod-verkoopbaar_warehouse">
                      {p.verkoopbaar_warehouse ?? Math.min(p.voorraad_warehouse ?? 0, p.moeder_voorraad_warehouse)}
                    </span>
                    {" "}(min. stickers en moeder-voorraad {p.moeder_voorraad_warehouse})
                  </div>
                )}
              </div>
              <div data-testid="voorraad-leverancier-cell">
                <Label className="text-xs text-muted-foreground">
                  {p.moeder_sku ? "Stickers leverancier" : "Voorraad leverancier"}
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-9 px-3 rounded-md border border-border bg-secondary/30 flex items-center font-medium tabular-nums text-lg" data-testid="prod-voorraad_leverancier">
                    {p.voorraad_leverancier ?? 0}
                  </div>
                  <VoorraadDeelCorrectie productId={id} deel="leverancier" deelLabel="Leverancier"
                                          huidig={p.voorraad_leverancier ?? 0} onDone={load} />
                </div>
                {p.moeder_sku && p.moeder_voorraad_leverancier != null && (
                  <div className="text-xs mt-1.5 text-muted-foreground">
                    Verkoopbaar: <span className="font-medium tabular-nums text-foreground" data-testid="prod-verkoopbaar_leverancier">
                      {p.verkoopbaar_leverancier ?? Math.min(p.voorraad_leverancier ?? 0, p.moeder_voorraad_leverancier)}
                    </span>
                    {" "}(min. stickers en moeder-voorraad {p.moeder_voorraad_leverancier})
                  </div>
                )}
              </div>
              {intField("Geplaatste order fabrikant", "voorraad_china")}
              {weekField("Binnenkomst order fabrikant (wk)", "binnenkomst_week")}
              {intField("Productie-leadtime (weken)", "productie_leadtime_weken")}
              {intField("Minimum (handmatig)", "minimum_voorraad")}
            </div>

            {(p.voorraad_china || 0) > 0 && (
              <div className="mt-4 rounded-lg border border-border bg-secondary/30 p-3 space-y-2"
                data-testid="china-inboeken-blok">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Order bij de fabrikant inboeken
                </div>
                <div className="text-xs text-muted-foreground">
                  {p.voorraad_china} stuks staan open{p.china_binnenkomst ? ` · verwacht ${fmtDate(p.china_binnenkomst)}` : ""}
                  {p.china_te_laat ? " · te laat" : ""}. Bij volledig inboeken worden de levermaand en het weeknummer
                  weer leeggemaakt en verdwijnt de order uit de herinneringsmail.
                </div>
                <ChinaInboeken productId={id} aantal={p.voorraad_china} onDone={load} />
              </div>
            )}

            {(p.gestickerde_varianten || []).length > 0 && (
              <div className="mt-4 rounded-lg border border-border bg-secondary/30 p-3" data-testid="gestickerde-varianten">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  Gestickerde varianten ({p.gestickerde_varianten.length}) — verbruiken deze moeder-voorraad bij verkoop
                </div>
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground">
                    <tr><th className="text-left py-1 pr-4">SKU</th><th className="text-left py-1 pr-4">Naam</th>
                        <th className="text-right py-1 pr-4">Stickers WH</th><th className="text-right py-1">Stickers Leverancier</th></tr>
                  </thead>
                  <tbody>
                    {p.gestickerde_varianten.map(v => (
                      <tr key={v.sku} className="border-t border-border/50">
                        <td className="py-1.5 pr-4">
                          {v.id ? (
                            <Link to={`/producten/${v.id}`} className="text-primary hover:underline font-medium" data-testid={`gest-variant-link-${v.sku}`}>{v.sku}</Link>
                          ) : (
                            <span className="font-medium">{v.sku}</span>
                          )}
                        </td>
                        <td className="py-1.5 pr-4 text-muted-foreground truncate max-w-xs">{v.naam || "—"}</td>
                        <td className="py-1.5 pr-4 text-right tabular-nums">{v.stickers_warehouse}</td>
                        <td className="py-1.5 text-right tabular-nums">{v.stickers_leverancier}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {p.moeder_sku && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="child-hint">
                <div className="font-medium text-amber-900">
                  Gestickerde variant van moeder:{" "}
                  {p.moeder_id ? (
                    <Link to={`/producten/${p.moeder_id}`} className="underline" data-testid="moeder-link">{p.moeder_sku}</Link>
                  ) : (
                    <span className="font-mono">{p.moeder_sku}</span>
                  )}
                </div>
                <div className="text-xs text-amber-800 mt-1">De voorraad hierboven telt alleen stickers. Bij verkoop wordt óók 1 moederproduct fysiek afgeboekt.</div>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-3 border-t border-border">
              <div>
                <Label className="text-xs text-muted-foreground">Geproduceerd in China</Label>
                <div className="flex gap-2 mt-1">
                  <Button type="button" size="sm" variant={p.china_geproduceerd ? "default" : "outline"}
                    onClick={()=>{ if (!p.china_geproduceerd) patch("china_geproduceerd", true); }} data-testid="prod-china-ja">Ja</Button>
                  <Button type="button" size="sm" variant={!p.china_geproduceerd ? "default" : "outline"}
                    onClick={()=>{ if (p.china_geproduceerd) patch("china_geproduceerd", false); }} data-testid="prod-china-nee">Nee</Button>
                </div>
              </div>
              {p.china_geproduceerd && intField("Productieweken (eerste N v.d. leadtime)", "productie_weken")}
              {p.china_geproduceerd && (
                <div className="flex items-end text-xs text-muted-foreground">
                  Houdt rekening met Chinees Nieuwjaar: 2 wk vóór t/m 4 wk ná is distributie ontregeld.
                </div>
              )}
            </div>
            <div className="pt-4 border-t border-border"><VoorraadCorrectie productId={id} onDone={load} /></div>
            <div className="pt-4 border-t border-border"><VoorraadForecast forecast={p.forecast}
              historieWeken={historieWeken} onHistorieChange={setHistorieWeken}
              productId={id} onIngeboekt={load}
              modus={modus} onModusChange={kiesModus} /></div>
            <div className="pt-4 border-t border-border"><Seizoenspatroon productId={id} /></div>
          </div>

          <div className="bg-card border border-border rounded-xl p-6 space-y-3">
            <div className="text-sm font-medium">Samengesteld product (stuklijst)</div>
            <div className="flex items-center gap-3">
              <Switch checked={!!p.is_samengesteld}
                onCheckedChange={(v) => patch("is_samengesteld", v)} data-testid="samengesteld-switch" />
              <p className="text-xs text-muted-foreground">
                Verkoop van dit product telt (× aantal) mee in de verkoop en forecast van elk component.
              </p>
            </div>
            {p.is_samengesteld && (
              <SamengesteldEditor componenten={p.componenten}
                onChange={(next) => patch("componenten", next)} />
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-xl font-medium">Voorraadmutaties</h3>
              <div className="text-xs text-muted-foreground">Audit trail — pakbonnen, bol.com, correcties, terugboekingen, historie</div>
            </div>
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left p-3">Datum</th>
                    <th className="text-left p-3">Deel</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-right p-3">Aantal</th>
                    <th className="text-left p-3">Bron / referentie</th>
                    <th className="text-left p-3">Klant</th>
                    <th className="text-left p-3">Notitie</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {muts.map(m => {
                    const typeVariant = m.type === "verkoop" ? "destructive"
                      : m.type === "inkoop" ? "default"
                      : "outline";
                    const isBol = (m.referentie || "").toLowerCase().startsWith("bol-repl") || (m.notitie || "").toLowerCase().includes("bol.com");
                    const isTerugboek = (m.referentie || "").toLowerCase().startsWith("terugboeking");
                    const isOmgestickerd = !!m.moeder_afboek_voor_child_sku;
                    return (
                      <tr key={m.id} data-testid={`mut-row-${m.id}`} className={isTerugboek ? "bg-emerald-50/40" : (m.directe_levering ? "bg-sky-50/50" : (isOmgestickerd ? "bg-amber-50/40" : ""))}>
                        <td className="p-3 text-sm whitespace-nowrap">{fmtDateTime(m.datum)}</td>
                        <td className="p-3 text-sm text-muted-foreground capitalize">{m.voorraad_deel || "—"}</td>
                        <td className="p-3">
                          <Badge variant={typeVariant} className="capitalize">{m.type}</Badge>
                          {isOmgestickerd && <Badge variant="outline" className="ml-1 text-[10px] border-amber-300 text-amber-800 bg-amber-50" data-testid={`mut-omgestickerd-${m.id}`}>omgestickerd</Badge>}
                          {m.directe_levering && (
                            <Badge variant="outline" className="ml-1 text-[10px] border-sky-300 text-sky-800 bg-sky-50"
                              title="Deze klant wordt direct uit de binnenkomende zending geleverd; deze verkoop is niet van de voorraadstand afgehaald."
                              data-testid={`mut-direct-${m.id}`}>
                              direct geleverd
                            </Badge>
                          )}
                        </td>
                        <td className={`p-3 text-right font-medium tabular-nums ${m.aantal < 0 ? "text-destructive" : "text-emerald-600"}`}>
                          {m.aantal > 0 ? "+" : ""}{m.aantal}
                        </td>
                        <td className="p-3 text-sm">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {isBol && <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">bol.com</Badge>}
                            {m.moeder_afboek_voor_child_sku && (
                              <span className="text-[10px] text-amber-800">
                                als <span className="font-mono">{m.moeder_afboek_voor_child_sku}</span>
                              </span>
                            )}
                            {m.verkoopkans_id ? (
                              <Link to={`/verkoopkansen/${m.verkoopkans_id}`}
                                className="text-primary hover:underline font-mono text-xs inline-flex items-center gap-1"
                                data-testid={`mut-kans-link-${m.id}`}>
                                {m.referentie || "kans"} <ExternalLink className="h-3 w-3" />
                              </Link>
                            ) : (
                              <span className="font-mono text-xs text-muted-foreground">{m.referentie || "—"}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-sm">
                          {m.organisatie_naam ? (
                            <Link to={`/organisaties/${m.organisatie_id}`}
                              className="text-primary hover:underline inline-flex items-center gap-1 max-w-[16rem] truncate"
                              data-testid={`mut-klant-link-${m.id}`}
                              title={m.verkoopkans_titel ? `${m.organisatie_naam} — ${m.verkoopkans_titel}` : m.organisatie_naam}>
                              {m.organisatie_naam} <ExternalLink className="h-3 w-3 shrink-0" />
                            </Link>
                          ) : isBol ? (
                            <span className="text-xs text-muted-foreground">bol.com</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="p-3 text-xs text-muted-foreground max-w-sm truncate" title={m.notitie || ""}>{m.notitie || ""}</td>
                      </tr>
                    );
                  })}
                  {muts.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground text-sm">Nog geen mutaties.</td></tr>}
                </tbody>
              </table>
            </div>
            {mutTotaal > 20 && (
              <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground"
                data-testid="mut-paginering">
                <span>
                  {mutPagina * 20 + 1}–{Math.min(mutTotaal, (mutPagina + 1) * 20)} van {mutTotaal} mutaties
                  (nieuwste eerst)
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={mutPagina === 0}
                    onClick={() => setMutPagina(mutPagina - 1)} data-testid="mut-vorige">
                    <ChevronLeft className="h-4 w-4" /> Nieuwer
                  </Button>
                  <span>{mutPagina + 1} / {Math.ceil(mutTotaal / 20)}</span>
                  <Button variant="outline" size="sm" disabled={(mutPagina + 1) * 20 >= mutTotaal}
                    onClick={() => setMutPagina(mutPagina + 1)} data-testid="mut-volgende">
                    Ouder <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {peilTotaal > 0 && (
            <div data-testid="voorraad-peilingen">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-xl font-medium">Historische voorraadstanden</h3>
                <div className="text-xs text-muted-foreground">Geïmporteerde peilingen uit oude voorraadlijsten</div>
              </div>
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left p-3">Datum</th>
                      <th className="text-left p-3">Deel</th>
                      <th className="text-right p-3">Voorraad</th>
                      <th className="text-left p-3">Artikelcode</th>
                      <th className="text-left p-3">Bestand</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {peilingen.map((pl) => (
                      <tr key={pl.id} data-testid={`peiling-row-${pl.id}`}>
                        <td className="p-3 text-sm whitespace-nowrap">{fmtDate(pl.datum)}</td>
                        <td className="p-3 text-sm text-muted-foreground capitalize">{pl.deel}</td>
                        <td className="p-3 text-right font-medium tabular-nums">{pl.aantal}</td>
                        <td className="p-3 text-xs font-mono text-muted-foreground">{pl.code || pl.sku || "—"}</td>
                        <td className="p-3 text-xs text-muted-foreground truncate max-w-xs">{pl.bestand || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {peilTotaal > 20 && (
                <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground"
                  data-testid="peiling-paginering">
                  <span>{peilPagina * 20 + 1}–{Math.min(peilTotaal, (peilPagina + 1) * 20)} van {peilTotaal}</span>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={peilPagina === 0}
                      onClick={() => setPeilPagina(peilPagina - 1)} data-testid="peiling-vorige">
                      <ChevronLeft className="h-4 w-4" /> Nieuwer
                    </Button>
                    <span>{peilPagina + 1} / {Math.ceil(peilTotaal / 20)}</span>
                    <Button variant="outline" size="sm" disabled={(peilPagina + 1) * 20 >= peilTotaal}
                      onClick={() => setPeilPagina(peilPagina + 1)} data-testid="peiling-volgende">
                      Ouder <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* 3. FINANCIEEL EN BESCHIKBAARHEID */}
        <TabsContent value="financieel" className="mt-4 space-y-6">
          <div className="bg-card border border-border rounded-xl p-6 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="font-medium">Beschikbaar voor verkoop</div>
                <div className="text-xs text-muted-foreground">
                  Pauzeer verkoop zonder te archiveren. Uit = product staat wél in het systeem,
                  maar de webshop toont hem als niet-op-voorraad en er kan niet worden besteld.
                </div>
              </div>
              <Switch checked={p.beschikbaar !== false}
                onCheckedChange={(v) => patch("beschikbaar", v)}
                data-testid="prod-beschikbaar" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t border-border">
              {numField("Inkoopprijs (€)", "inkoopprijs")}
              {numField("Standaard verkoopprijs (€)", "verkoopprijs")}
              {numField("Consument-adviesprijs (€)", "consument_adviesprijs")}
              <div>
                <Label className="text-xs text-muted-foreground">BTW</Label>
                <Select value={String(p.btw_tarief || "21")} onValueChange={(v)=>patch("btw_tarief", v)}>
                  <SelectTrigger data-testid="prod-btw"><SelectValue /></SelectTrigger>
                  <SelectContent>{["21","9","0"].map(b=><SelectItem key={b} value={b}>{b}%</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground pt-4 border-t border-border">
              <div>Marge per stuk: <span className="text-foreground font-medium">{fmtEUR(p.marge_per_stuk || 0)}</span></div>
              <div>Marge %: <span className="text-foreground font-medium">{p.marge_pct != null ? `${p.marge_pct}%` : "—"}</span></div>
              <div>Marge dit jaar (YTD): <span className="text-foreground font-medium">{fmtEUR(p.marge_dit_jaar || 0)}</span></div>
              <div>Verkocht YTD: <span className="text-foreground font-medium">{p.verkocht_ytd || 0} stuks</span></div>
            </div>
            <div className="pt-4 border-t border-border">
              <LandprijzenBlok p={p} patch={patch} />
            </div>
            <div className="pt-4 border-t border-border">
              <div className="text-sm font-medium mb-1">Moneybird — grootboek voor omzet</div>
              <p className="text-xs text-muted-foreground mb-3">
                Kies per verkoop-regime het Moneybird-grootboek. Het regime wordt bepaald door het
                leveradres van de klant: <strong>Nederland</strong>, <strong>binnen EU</strong> (BTW verlegd)
                of <strong>buiten EU</strong> (export, 0%). Laat op default staan voor het standaard-omzetgrootboek.
                Beheer welke grootboeken hier verschijnen via
                {" "}<Link to="/instellingen/moneybird" className="text-primary hover:underline">Instellingen → Moneybird</Link>.
              </p>
              <div className="space-y-3">
                <div>
                  <div className="text-xs font-medium mb-1">Nederland</div>
                  <LedgerKeuze value={p.moneybird_ledger_account_id || ""}
                                onChange={(v) => patch("moneybird_ledger_account_id", v)}
                                testId="prod-moneybird-ledger" />
                </div>
                <div>
                  <div className="text-xs font-medium mb-1">Binnen EU (BTW verlegd)</div>
                  <LedgerKeuze value={p.moneybird_ledger_account_id_eu || ""}
                                onChange={(v) => patch("moneybird_ledger_account_id_eu", v)}
                                testId="prod-moneybird-ledger-eu" />
                </div>
                <div>
                  <div className="text-xs font-medium mb-1">Buiten EU (export, 0%)</div>
                  <LedgerKeuze value={p.moneybird_ledger_account_id_export || ""}
                                onChange={(v) => patch("moneybird_ledger_account_id_export", v)}
                                testId="prod-moneybird-ledger-export" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Tip: als binnen-EU of buiten-EU leeg is, valt de push automatisch terug op het Nederland-grootboek.
              </p>
            </div>
          </div>
        </TabsContent>

        {/* 4. VERKOOPPLATFORMS — Bol.com */}
        <TabsContent value="platforms" className="mt-4 space-y-6">
          <div className="bg-card border border-border rounded-xl p-6 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Languages className="h-4 w-4 text-primary" /> Platform-teksten per taal (bol.com e.d.)
            </div>
            <p className="text-xs text-muted-foreground">
              Aparte commerciële teksten voor verkoopplatforms — een andere doelgroep (B2C-marktplaats) dan de
              eigen webshop. Deze staan los van de website-teksten onder “Commerciële data”. Claude schrijft hier
              platform-gericht; claims moeten per taal gevalideerd worden.
            </p>
            <Productteksten value={p.platform_teksten} veld="platform_teksten" productId={p.id}
              onChange={(next) => patch("platform_teksten", next)} />
          </div>
          <div className="bg-card border border-border rounded-xl p-6 space-y-5">
            <div className="flex items-center gap-2 text-sm font-medium mb-1">
              <ShoppingBag className="h-4 w-4 text-primary" /> Bol.com koppelingen
            </div>
            <p className="text-xs text-muted-foreground">
              Koppel dit product aan bol.com-EAN's per verpakkingsniveau. Bij een LVB-verkoop
              boekt de sync <strong>aantal × stuks per pack</strong> af van je warehouse.
              Bijvoorbeeld: 1× inner-pack verkocht met 6 stuks/pack = 6 warehouse afgeboekt.
            </p>
            <BolKoppelingenEditor
              koppelingen={p.bol_koppelingen || []}
              innerVerpakking={p.inner_verpakking}
              outerVerpakking={p.outer_verpakking}
              onChange={(next) => patch("bol_koppelingen", next)}
            />
          </div>
        </TabsContent>

        {/* 5. LOGISTIEKE DATA */}
        <TabsContent value="logistiek" className="mt-4 space-y-6">
          <div className="bg-card border border-border rounded-xl p-6 space-y-5">
            <div>
              <div className="text-sm font-medium mb-2">Afmetingen</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {numField("Lengte (cm)", "afmeting_lengte", "0.1")}
                {numField("Breedte (cm)", "afmeting_breedte", "0.1")}
                {numField("Hoogte (cm)", "afmeting_hoogte", "0.1")}
                {numField("Gewicht (gram)", "gewicht_gram", "1")}
              </div>
            </div>
            <div className="pt-4 border-t border-border">
              <div className="text-sm font-medium mb-2">Verpakking</div>
              <div className="grid grid-cols-2 gap-3">
                {intField("Inner packaging (stuks)", "inner_verpakking")}
                {intField("Outer packaging (stuks)", "outer_verpakking")}
              </div>
            </div>
            <div className="pt-4 border-t border-border">
              <div className="flex items-center gap-2 text-sm font-medium mb-1">
                <Languages className="h-4 w-4 text-primary" /> Vertalingen (buitenlandse facturatie)
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Bij een buitenlandse klant (land ≠ Nederland) wordt op factuur/pakbon de vertaalde artikelnaam gebruikt:
                eerst de landspecifieke vertaling, anders de Engelse standaardnaam.
              </p>
              <div className="max-w-md">
                {txtField("Artikelnaam (Engels — standaard)", "naam_en", "English product name")}
              </div>
              <div className="mt-4">
                <Label className="text-xs text-muted-foreground">Landspecifieke vertalingen</Label>
                <div className="mt-2">
                  <VertalingenEditor vertalingen={p.vertalingen}
                    onChange={(next) => patch("vertalingen", next)} />
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-border">
                <Label className="text-xs text-muted-foreground font-medium">Webshop-vertalingen (naam + omschrijving per taal)</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Voor de externe webshop-API. Leeg laten = webshop toont Nederlandse tekst.
                  Extra talen kunnen later worden toegevoegd.
                </p>
                <div className="mt-2">
                  <ShopVertalingenEditor value={p.vertalingen_i18n || {}}
                    fallbackNaamEn={p.naam_en || ""}
                    onChange={(next) => patch("vertalingen_i18n", next)} />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* 6. COMMERCIËLE DATA — productteksten, claims, marketing + sales stats */}
        <TabsContent value="verkoopdata" className="mt-4 space-y-6">
          <div className="bg-card border border-border rounded-xl p-6 space-y-5">
            <div>
              <div className="text-sm font-medium mb-1">Productgegevens voor productsheet</div>
              <p className="text-xs text-muted-foreground mb-3">Gebruikt door de Word/PDF-productsheet (taal-onafhankelijk).</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {txtField("Inhoud", "inhoud", "Bijv. 75 ml / 12 stuks")}
                {numField("Besteleenheid (stuks per verpakking)", "bestelbaar_per", "1")}
                {intField("Minimale afname (0 = geen minimum)", "minimale_afname")}
                <div>
                  <Label className="text-xs text-muted-foreground">Productcategorie webshop</Label>
                  <Input
                    list="webshop-categorieen-list"
                    defaultValue={p.webshop_categorie || ""}
                    placeholder="Bijv. Mondzorg, Huidverzorging…"
                    onBlur={(e) => {
                      const v = (e.target.value || "").trim();
                      if (v !== (p.webshop_categorie || "")) patch("webshop_categorie", v);
                    }}
                    data-testid="prod-webshop-categorie"
                  />
                  <datalist id="webshop-categorieen-list">
                    {webshopCategorieen.map((c) => <option key={c} value={c} />)}
                  </datalist>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Voor de indeling van producten op de webshop.
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-border grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">USP — één korte verkoopzin (webshop-winkelschap)</Label>
                  <Input
                    key={`usp-${p.id}`}
                    defaultValue={p.usp || ""}
                    maxLength={90}
                    placeholder="Bijv. Verzorgt en matteert de gecombineerde huid"
                    onBlur={(e) => { const v = (e.target.value || "").trim(); if (v !== (p.usp || "")) patch("usp", v); }}
                    data-testid="prod-usp"
                  />
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Max. 90 tekens. Komt op het prijskaartje van het virtuele winkelschap. Per taal in te vullen bij
                    de productteksten hieronder.
                  </div>
                </div>
                <div className="flex items-start gap-3 pt-5">
                  <Switch
                    checked={p.toon_op_homepage === true}
                    onCheckedChange={(v) => patch("toon_op_homepage", v)}
                    data-testid="prod-toon-op-homepage"
                  />
                  <div>
                    <div className="text-sm">Op het winkelschap van de homepage</div>
                    <div className="text-[11px] text-muted-foreground">
                      Publiek zichtbaar met naam, foto en categorie — nooit prijs of voorraad.
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-border">
                <Label className="text-xs text-muted-foreground">Ingrediënten (INCI)</Label>
                <Textarea
                  key={`ing-${p.id}`}
                  defaultValue={p.ingredienten || ""}
                  onBlur={(e) => {
                    const v = (e.target.value || "").trim();
                    if (v !== (p.ingredienten || "")) patch("ingredienten", v);
                  }}
                  placeholder="Aqua, Glycerin, Cetearyl Alcohol, Butyrospermum Parkii (Shea) Butter, Tocopherol…"
                  rows={4}
                  className="font-mono text-xs"
                  data-testid="prod-ingredienten"
                />
                <div className="text-[11px] text-muted-foreground mt-1">
                  INCI-lijst (Latijnse namen, aflopend op concentratie). Wettelijk verplicht voor cosmetica.
                  De webshop toont dit in een transparantie-blok. Ingrediënten zijn hetzelfde in elk land.
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-border">
              <div className="flex items-center gap-2 text-sm font-medium mb-1">
                <Languages className="h-4 w-4 text-primary" /> Website-teksten per taal
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Producttekst, claims en huidtype per taal voor de eigen webshop. Nederlands is de standaard.
                Platform-teksten (bol.com) staan apart onder “Verkoopplatforms”.
              </p>
              <Productteksten value={p.productteksten} veld="productteksten" productId={p.id} onChange={(next) => patch("productteksten", next)} />
            </div>
            <div className="flex items-center gap-2 pt-4 border-t border-border flex-wrap">
              <FileText className="h-4 w-4 text-primary" />
              <span className="text-sm">Genereer een productsheet:</span>
              <Button variant="outline" size="sm"
                onClick={() => downloadFile(`/producten/${id}/productsheet?format=docx`, `productsheet-${p.sku}.docx`).catch(() => toast.error("Download mislukt"))}
                data-testid="productsheet-word">
                <FileText className="h-4 w-4 mr-1" /> Word
              </Button>
              <Button variant="outline" size="sm"
                onClick={() => downloadFile(`/producten/${id}/productsheet?format=pdf`, `productsheet-${p.sku}.pdf`).catch((e) => toast.error(e?.response?.data?.detail || "PDF niet beschikbaar"))}
                data-testid="productsheet-pdf">
                <FileText className="h-4 w-4 mr-1" /> PDF
              </Button>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Verkocht (12 mnd)</div>
                <div className="font-display text-2xl font-medium mt-1">{p.verkocht_12m ?? 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Gem. verkoop/mnd</div>
                <div className="font-display text-2xl font-medium mt-1">{p.gem_maandverkoop ?? 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Voorraaddekking</div>
                <div className="font-display text-2xl font-medium mt-1">{p.voorraad_dekking_weken != null ? `${p.voorraad_dekking_weken} wk` : "—"}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Besteladvies</div>
                <div className={`font-display text-2xl font-medium mt-1 ${p.bestel_advies_aantal > 0 ? "text-accent" : ""}`}>{p.bestel_advies_aantal > 0 ? `${p.bestel_advies_aantal}` : "—"}</div>
              </div>
            </div>
          </div>

          )}
        </TabsContent>

        {/* 7. FOTO'S */}
        <TabsContent value="fotos" className="mt-4 space-y-6">
          <ProductFotos productId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
